import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBolt, faPuzzlePiece, faLayerGroup, faListUl,
    faIdCard, faShieldAlt
} from '@fortawesome/free-solid-svg-icons';

import ModuleABM          from './ModuleABM';
import GroupABM           from './GroupABM';
import MenuComposer       from './MenuComposer';
import ProfileABM         from './ProfileABM';
import ProfilePermissions from './ProfilePermissions';

/**
 * MenuBuilderPanel — Container for all Phase 3 menu management sub-modules.
 * Rendered inside SuperAdminPanel when tab === 'menubuilder'.
 */
const TABS = [
    { id: 'modules',  label: 'Módulos',   icon: faPuzzlePiece, desc: 'Pantallas y vistas del sistema' },
    { id: 'groups',   label: 'Grupos',    icon: faLayerGroup,  desc: 'Secciones del sidebar' },
    { id: 'composer', label: 'Compositor', icon: faListUl,     desc: 'Asignación Módulo → Grupo' },
    { id: 'profiles', label: 'Perfiles',  icon: faIdCard,      desc: 'Roles de usuario' },
    { id: 'perms',    label: 'Permisos',  icon: faShieldAlt,   desc: 'Matriz de acceso por perfil' },
];

const MenuBuilderPanel = ({ ipserv }) => {
    const [activeTab, setActiveTab] = useState('modules');

    const renderTab = () => {
        switch (activeTab) {
            case 'modules':  return <ModuleABM />;
            case 'groups':   return <GroupABM />;
            case 'composer': return <MenuComposer />;
            case 'profiles': return <ProfileABM />;
            case 'perms':    return <ProfilePermissions />;
            default:         return <ModuleABM />;
        }
    };

    return (
        <div className="animate-in pb-5">
            {/* Page header */}
            <div className="mb-4">
                <h2 style={{ fontWeight: 800, margin: 0 }}>
                    <FontAwesomeIcon icon={faBolt} style={{ color: 'var(--sa-gold)', marginRight: '0.6rem' }} />
                    Menu Builder
                </h2>
                <p style={{ color: '#cbd5e1' }} className="small mt-1 mb-0">
                    Sistema dinámico de menús, perfiles y permisos de acceso
                </p>
            </div>

            {/* Inner tab bar */}
            <div style={{
                display: 'flex', gap: '0.25rem', flexWrap: 'wrap',
                background: 'rgba(15,23,42,0.5)', borderRadius: 16,
                padding: '6px', marginBottom: '1.75rem',
                border: '1px solid rgba(168,85,247,0.1)'
            }}>
                {TABS.map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '0.5rem',
                            padding: '0.6rem 1.1rem', border: 'none', borderRadius: 12,
                            cursor: 'pointer', transition: 'all 0.2s',
                            background: activeTab === tab.id
                                ? 'linear-gradient(135deg, var(--sa-primary), var(--sa-primary-dark))'
                                : 'transparent',
                            color: activeTab === tab.id ? 'white' : '#cbd5e1',
                            fontWeight: activeTab === tab.id ? 700 : 500,
                            fontSize: '0.82rem',
                            boxShadow: activeTab === tab.id ? '0 4px 12px var(--sa-primary-glow)' : 'none'
                        }}
                    >
                        <FontAwesomeIcon icon={tab.icon} style={{ fontSize: '0.85rem' }} />
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Tab description strip */}
            <div style={{
                background: 'rgba(168,85,247,0.04)',
                border: '1px solid rgba(168,85,247,0.1)',
                borderRadius: 12, padding: '0.7rem 1.25rem',
                marginBottom: '1.5rem',
                display: 'flex', alignItems: 'center', gap: '0.75rem'
            }}>
                <FontAwesomeIcon
                    icon={TABS.find(t => t.id === activeTab)?.icon || faBolt}
                    style={{ color: 'var(--sa-primary)', fontSize: '1rem' }}
                />
                <span style={{ color: '#cbd5e1', fontSize: '0.85rem' }}>
                    <strong style={{ color: '#f8fafc' }}>{TABS.find(t => t.id === activeTab)?.label}</strong>
                    {' — '}{TABS.find(t => t.id === activeTab)?.desc}
                </span>
            </div>

            {/* Content */}
            <AnimatePresence mode="wait">
                <motion.div
                    key={activeTab}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.2 }}
                >
                    {renderTab()}
                </motion.div>
            </AnimatePresence>
        </div>
    );
};

export default MenuBuilderPanel;
